import { lazy, useEffect, useState } from "react";
import { Alert, Box, Dialog, LinearProgress, Typography } from "@mui/material";

import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useDispatch, useSelector } from "react-redux";

import { RootState, store } from "@store/store";
import { setPrintingReset } from "@store/slices/componentsSlice";

const General = lazy(() => import("@features/Solicitud/subFeatures/InfoGeneral/components/general/General"));
const Perfil = lazy(() => import("@features/Solicitud/subFeatures/PerfilFinanciero/components/sections/Perfil"));
const Dashboard = lazy(() => import("@features/Solicitud/subFeatures/PerfilFinanciero/components/sections/Dashboard"));

import { Heimdall } from "@components/Heimdall";
import { DialogProps } from "@toolpad/core/useDialogs";
import { DialogoBaseProps } from "@interfaces/components";
import { TablaBalanceGeneral } from "@features/Solicitud/subFeatures/PerfilFinanciero/components/TablaBalanceGeneral";
import { useLazyGetFileCategoriesQuery } from "@services/api/catalogs";
import { TablaEstadosResultados } from "@features/Solicitud/subFeatures/PerfilFinanciero/components/TablaEstadoResultados";
import { usePostBalanceSheetMutation, usePostIncomeStatementMutation } from "@services/api/sat_core";
import {
    useLazyGetDocumentsQuery,
    useLazyGetSignUrlToUploadDocQuery,
    usePatchDocumentMutation,
    usePostDocumentMutation,
    usePostUploadFileMutation,
} from "@services/api/documents";

interface DescargarReporte extends DialogoBaseProps {}

export const DescargarReporte = (props: DialogProps<DescargarReporte, boolean>) => {
    const dispatch = useDispatch();

    const [progress, setProgress] = useState<number>(0);

    const permissions = (state = store.getState()) => state.session.permissions;
    const applicationSelected = (state = store.getState()) => state.components.applicationSelected;

    const userSession = useSelector((state: RootState) => state.session.user);

    const printingProfileReport = useSelector((state: RootState) => state.components.printingProfileReport);

    const [triggerGetDocs] = useLazyGetDocumentsQuery();
    const [triggerPostDocument] = usePostDocumentMutation();
    const [triggerPatchDocument] = usePatchDocumentMutation();
    const [triggerPostUploadDoc] = usePostUploadFileMutation();
    const [triggerGetDocsCategory] = useLazyGetFileCategoriesQuery();
    const [triggetPostUrlToUploadDoc] = useLazyGetSignUrlToUploadDocQuery();

    const [triggerPostBalanceSheet, balanceSheet] = usePostBalanceSheetMutation();
    const [triggerPostIncomeStatement, incomeStatement] = usePostIncomeStatementMutation();

    const saveDoc = async (doc: Blob) => {
        try {
            const categories = await triggerGetDocsCategory("").unwrap();
            const pldCategory = categories.find((category) => category.code === "DPCR");

            if (pldCategory === undefined) return;

            const urlToUploadResponse = await triggetPostUrlToUploadDoc({
                upload_type: "client",
                file_name: "Reporte_PerfilCrediticio.pdf",
                regimen: applicationSelected(store.getState()).tax_system.tax_system_type.toLowerCase(),
                person_id: undefined,
                person_type: undefined,
                client_details_id: applicationSelected(store.getState()).client_detail_id,
                file_category_id: pldCategory.id,
                loan_application_id: applicationSelected(store.getState()).id,
            }).unwrap();

            const signUrl = urlToUploadResponse.data;

            const tmp = new FormData();

            tmp.append("key", signUrl.fields.key);
            tmp.append("AWSAccessKeyId", signUrl.fields.AWSAccessKeyId);
            tmp.append("x-amz-security-token", signUrl.fields["x-amz-security-token"]);
            tmp.append("policy", signUrl.fields.policy);
            tmp.append("signature", signUrl.fields.signature);
            tmp.append("file", doc);

            await triggerPostUploadDoc({
                url: signUrl.url,
                body: tmp,
            }).unwrap();

            const currentDocs = await triggerGetDocs({
                loan_application_id: applicationSelected(store.getState()).id,
                client_details_id: applicationSelected(store.getState()).client_detail_id,
            }).unwrap();

            const currentPld = currentDocs.find((doc) => doc.file_code === "DPCR");

            if (currentPld !== undefined) {
                if (currentPld.validated) {
                    await triggerPatchDocument({
                        applicationId: applicationSelected(store.getState()).id,
                        documentId: currentPld.id ?? "0-0-0-0-0",
                        body: {
                            validated: false,
                            user_name:
                                userSession.name
                                    ?.concat(" ", userSession?.last_name ?? "", " ", userSession?.last_name_2 ?? "")
                                    .trim() ?? "",
                        },
                    }).unwrap();
                }
                await triggerPatchDocument({
                    applicationId: applicationSelected(store.getState()).id,
                    documentId: currentPld.id ?? "0-0-0-0-0",
                    body: {
                        status: false,
                        user_name:
                            userSession.name
                                ?.concat(" ", userSession?.last_name ?? "", " ", userSession?.last_name_2 ?? "")
                                .trim() ?? "",
                    },
                }).unwrap();
            }

            await triggerPostDocument({
                loan_application_id: applicationSelected(store.getState()).id ?? "0-0-0-0-0",
                file_category_id: pldCategory.id,
                url: signUrl.fields.key,
                file_name: "Reporte_PerfilCrediticio.pdf",
                client_details_id: applicationSelected(store.getState()).client_detail_id,
                person_id: undefined,
                user_name:
                    userSession.name
                        ?.concat(" ", userSession?.last_name ?? "", " ", userSession?.last_name_2 ?? "")
                        .trim() ?? "",
            }).unwrap();
        } catch (error) {
            console.log(error);
        }
    };

    const handleOnClick = async () => {
        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "A4",
            // compress: true,
        });

        const element = document.getElementById("temp");
        if (!element) return;

        const canvas = await html2canvas(element, { scale: 2 });
        const imgData = canvas.toDataURL("image/jpeg");

        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        const ratio = imgProps.width / pdfWidth;

        const cropWidth = imgProps.width;
        const cropHeight = 4500;

        let count = 0;
        let cropY = 0;
        while (cropY < imgProps.height) {
            setProgress(Math.round((cropY * 100) / imgProps.height));
            const canvasPage = document.createElement("canvas");
            canvasPage.width = imgProps.width;
            canvasPage.height = pdfHeight * ratio;
            const ctx = canvasPage.getContext("2d");
            if (ctx === null) continue;

            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(canvas, 0, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

            const tmp = canvasPage.toDataURL("image/jpeg");
            pdf.addImage(tmp, "JPEG", 10, 10, pdfWidth - 20, pdfHeight - 50);
            pdf.addPage();

            count += 1;
            cropY += cropHeight;
        }

        pdf.deletePage(count + 1);

        const pdfBlob = pdf.output("blob");
        await saveDoc(pdfBlob);
        await pdf.save(`ReporteSolicitud_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`, { returnPromise: true });

        dispatch(setPrintingReset());
        props.onClose(true);
    };

    const loadFinancialData = async (rfc: string, startDate: string, endDate: string) => {
        try {
            Promise.all([
                triggerPostBalanceSheet({
                    rfc: rfc,
                    startDate: startDate,
                    endDate: endDate,
                }),
                triggerPostIncomeStatement({
                    rfc: rfc,
                    startDate: startDate,
                    endDate: endDate,
                }),
            ]).finally(() => {});
        } catch (error) {
        } finally {
        }
    };

    useEffect(() => {
        if (userSession.role?.code === "RCJO" || userSession.role?.code === "RLEG2") {
            if (printingProfileReport.module.generalReady && printingProfileReport.module.downloadSatReady) {
                setProgress(20);
                setTimeout(() => {
                    handleOnClick();
                }, 3000);
            }
        } else {
            if (
                printingProfileReport.module.generalReady &&
                printingProfileReport.module.downloadSatReady &&
                printingProfileReport.module.profileReady &&
                printingProfileReport.module.dashboardReady &&
                // printingProfileReport.module.financialReady
                balanceSheet.isSuccess &&
                incomeStatement.isSuccess
            ) {
                setProgress(20);
                setTimeout(() => {
                    handleOnClick();
                }, 3000);
            }
        }
    }, [printingProfileReport, balanceSheet, incomeStatement]);

    useEffect(() => {
        if (
            applicationSelected(store.getState()).company_rfc === undefined ||
            applicationSelected(store.getState()).company_rfc === ""
        )
            return;

        const startDate = `${new Date().getFullYear() - 3}-01-01`;
        const endDate = `${new Date().getFullYear()}-12-31`;
        loadFinancialData(applicationSelected(store.getState()).company_rfc, startDate, endDate);
    }, [applicationSelected(store.getState()).company_rfc]);

    return (
        <Dialog fullScreen={props.payload.fullScreen} fullWidth={props.payload.fullWidth} open={props.open}>
            <Box minWidth={100}>
                <Alert severity="info">Generando reporte... {progress}%</Alert>
            </Box>
            <Box>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 10 }} />
            </Box>
            <Box overflow={"scroll"}>
                <Box id="temp">
                    <General />
                    <Heimdall scopes={["SBPCG"]}>
                        <Box height={"800px"}></Box>
                        <Perfil showOnlyCategoryAndVariables={false} />
                    </Heimdall>
                    <Heimdall scopes={["SBDASH"]}>
                        <Box height={"450px"}></Box>
                        <Dashboard />
                    </Heimdall>
                    <Heimdall scopes={["SBFINA"]}>
                        <Box height={"100px"}></Box>
                        <Typography variant="h4" fontWeight={600} color={"#002652"} mt={0} mb={1}>
                            Balance General
                        </Typography>
                        <TablaBalanceGeneral
                            data={balanceSheet.data}
                            isLoading={balanceSheet.isSuccess === false}
                            printingMode={true}
                        />
                    </Heimdall>
                    <Heimdall scopes={["SBFINA"]}>
                        <Box height={"600px"}></Box>

                        <Typography variant="h4" fontWeight={600} color={"#002652"} mt={6} mb={1}>
                            Estado de Resultados
                        </Typography>

                        <TablaEstadosResultados
                            data={incomeStatement.data}
                            isLoading={incomeStatement.isSuccess === false}
                            printingMode={true}
                        />
                    </Heimdall>
                </Box>
            </Box>
        </Dialog>
    );
};
